import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchCurrentUser,
  getAllUserInfoByEmail,
  loginUser,
  signUpUser,
  updateEmailOptIn,
} from '../actions/authActions';

// 1. Define State: Only Identity & Access
interface AuthState {
  userId: number;
  authToken: string;
  email: string;
  username: string;
  isLoggedIn: boolean;
  isStaff: boolean;
  isNewUser: boolean;
  isLoading: boolean;
  error: string | null;
  emailOptIn: boolean;
  emailOptInSaving: boolean;
  emailOptInError: string | null;
}

const initialState: AuthState = {
  userId: 0,
  authToken: '',
  email: '',
  username: '',
  isLoggedIn: false,
  isStaff: false,
  isNewUser: false,
  isLoading: false,
  error: null,
  emailOptIn: false,
  emailOptInSaving: false,
  emailOptInError: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: () => initialState, // Easy reset
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.authToken = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
  },
  extraReducers: builder => {
    // LOGIN
    builder.addCase(loginUser.pending, state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.authToken = action.payload?.auth_token;
      state.userId = action.payload?.id || state.userId;
      state.username = action.payload?.username || state.username;
      state.email = action.payload?.email || state.email;

      if (!action.payload?.preserveNewUserFlag) {
        state.isNewUser = false;
      }
    });
    builder.addCase(loginUser.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload || 'Login failed';
    });

    // SIGNUP
    builder.addCase(signUpUser.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isLoggedIn = true;
      state.userId = action.payload.id;
      state.username = action.payload.username;
      state.email = action.payload.email;
      state.emailOptIn = action.payload.email_opt_in ?? state.emailOptIn;
      state.isNewUser = true; // Specific to signup
    });

    // GET INFO (Hybrid Action - Auth Slice takes what it needs)
    // NOTE: only returns { id, username } when the requester owns the email.
    builder.addCase(getAllUserInfoByEmail.fulfilled, (state, action) => {
      const userRes = action.payload.user;
      if (userRes) {
        if (userRes.id) state.userId = userRes.id;
        if (userRes.username) state.username = userRes.username;
      }
    });

    // FETCH CURRENT USER (authenticated — sets isStaff after login)
    // Also the source of the user's id/username after login: the login
    // response only carries a token, and the email lookup no longer says
    // who an account belongs to.
    builder.addCase(fetchCurrentUser.fulfilled, (state, action) => {
      const user = action.payload;
      if (user) {
        state.userId = user.id ?? state.userId;
        state.username = user.username ?? state.username;
        state.email = user.email ?? state.email;
        state.isStaff = user.is_staff ?? state.isStaff;
        state.emailOptIn = user.email_opt_in ?? state.emailOptIn;
      }
    });

    // EMAIL OPT-IN (account settings toggle)
    builder.addCase(updateEmailOptIn.pending, state => {
      state.emailOptInSaving = true;
      state.emailOptInError = null;
    });
    builder.addCase(updateEmailOptIn.fulfilled, (state, action) => {
      state.emailOptInSaving = false;
      state.emailOptIn = action.payload?.email_opt_in ?? state.emailOptIn;
    });
    builder.addCase(updateEmailOptIn.rejected, (state, action: any) => {
      state.emailOptInSaving = false;
      state.emailOptInError = action.payload || 'Could not save your email preference. Please try again.';
    });
  },
});

export const { logout, setAuthToken, setEmail } = authSlice.actions;
export default authSlice.reducer;
